import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { atomFamily, selector, selectorFamily, useResetRecoilState } from 'recoil';

/**
 * Gets os sets the refresh state for a Recoil selector.
 * 
 * @param {string} key - selector key
 * @returns {number} Refresh count
 */
export const refreshState = atomFamily<number, string>({
    key: 'refreshState',
    default: () => 0
});

/**
 * Gets or sets the refresh state for a Recoil selector
 * 
 * @param {string} key - selector key
 * @returns {number} Refresh count
 */
export const refreshSelector = selectorFamily<number, string>({
    key: 'refreshSelector',
    get: (key) => ({ get }) => get(refreshState(key)),
    set: (key) => ({ set }) => set(refreshState(key), (n) => n + 1)
});

/**
 * Custom hook to refresh Recoil state that implements a refresh dependency.
 * 
 * @param {object} atom - Recoil atom[Family] or selector[Family]
 */
export function useSetRefreshState(atom: { key: string }) {
    return useResetRecoilState(refreshSelector(atom.key));
}

/**
 * Custom hook to refresh Recoil state that implements a refresh dependency.
 * 
 * @param {string} key - Refresh state key
 */
export function useSetRefreshStateKey(key: string) {
    return useResetRecoilState(refreshSelector(key));
}

/**
 * The autoRefresh selector is used to automatically refresh state.
 * The autoRefresh selector will be updated on every page load.
 * Add this selector as a dependency to other selectors that require
 * the same behavior.
 */
export const autoRefresh = selector<number>({
    key: 'autoRefresh',
    get: ({ get }) => get(refreshState('autoRefresh'))
});

/**
 * Custom hook to update the autoRefresh state.
 */
export function useAutoRefresh() {
    const refresh = useSetRefreshState(autoRefresh);
    const { pathname } = useLocation();

    useEffect(() => {
        if (refresh) {
            refresh();
        }
    }, [pathname, refresh]);
}
